import { useRef, useState } from "react";
import { CTAButton } from "lib/components/CTAButton";
import { CheckboxButton } from "lib/components/Checkbox";
import { KeyValue } from "lib/components/KeyValue";
import { fetchData } from "lib/helpers/fetchData";
import { getApiUrl } from "lib/helpers/getApiUrl";
import { getFormValues } from "lib/helpers/getFormValues";
import { StyledForm } from "lib/styles/general";
import { generateRequestTypeMappings } from "lib/jira/requestTypeMapping";
import { ErrorModal } from "lib/components/Modal";

export const ServiceToggle = ({ data, reload, appKey, shouldGenerateRequestTypeMappings = false }) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState();

  const formRef = useRef(null);

  const enabled = !!data?.apps?.[appKey]?.enabled;
  const enabledStatus = enabled ? "Enabled" : "Disabled";

  const generateRequestTypeMappingsOnEnableChange = async (isEnabled) => {
    if (shouldGenerateRequestTypeMappings && isEnabled) {
      const { instanceId, projectId } = data.connectors.jira;
      return generateRequestTypeMappings(data.id, projectId, instanceId);
    }

    return { success: true };
  };

  const updateServiceSettings = async (isEnabled) => {
    try {
      await fetchData(`${getApiUrl("ADMIN_API")}/customers/${data?.id}/apps/${appKey}`, JSON.stringify({ enabled: isEnabled }), "PUT");
    } catch (error) {
      return { success: false, errorMessage: error?.error?.message ?? "An error occurred whilst attempting to update the customer's service configuration. Please try again or contact the development team." };
    }

    return { success: true };
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let { enabled } = getFormValues(formRef.current);

    const generateMappingResult = await generateRequestTypeMappingsOnEnableChange(enabled);

    if (generateMappingResult.success === false) {
      setError(generateMappingResult.errorMessage);
    } else {
      const updateServiceResult = await updateServiceSettings(enabled);

      if (updateServiceResult.success === false) {
        setError(updateServiceResult.errorMessage);
      } else {
        setIsEditMode(false);
        reload();
      }
    }

    setIsLoading(false);
  };

  return (
    <div style={{ position: "relative" }}>
      <StyledForm ref={formRef}>
        <section style={{ paddingTop: 0 }}>
          <KeyValue
            text="Service Status"
            wrap={false}
            value={
              <CheckboxButton
                name="enabled"
                subtle
                showWhenNull
                label={isEditMode ? "Is Enabled?" : enabledStatus}
                defaultChecked={enabled}
                readOnly={!isEditMode}
              />
            }
          />
        </section>
      </StyledForm>
      <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "1rem" }}>
        {isEditMode ? (
          <>
            <CTAButton isSecondary onClick={() => setIsEditMode(false)} disabled={isLoading}>
              Cancel
            </CTAButton>
            <CTAButton isLoading={isLoading} onClick={handleSubmit}>
              Save
            </CTAButton>
          </>
        ) : (
          <CTAButton onClick={() => setIsEditMode(true)}>Edit</CTAButton>
        )}
        {error && <ErrorModal data-testid="error" error={error} hide={() => setError(null)} />}
      </div>
    </div>
  );
};
